import { CaretRight } from "@phosphor-icons/react";
import { useMemo, useState } from "react";

import AimfoxAvatar from "common/components/ui/AimfoxAvatar";
import { Button } from "common/components/ui/Button";
import { Dialog, OverlayDialogContent } from "common/components/ui/Dialog";

import { formatBillingDate } from "../../../utils";
import MobileListTable from "../../MobileListTable";
import MobileClientDetails from "./MobileClientDetails";

import type { ColumnDef } from "@tanstack/react-table";
import type { AgencyClient } from "../../../types";

export default function MobileList({ clients }: { clients: AgencyClient[] }) {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<AgencyClient>(null);

  const handleRowClick = (client: AgencyClient) => {
    setSelectedClient(client);
    setIsDetailsOpen(true);
  };

  const columns = useMemo(
    () =>
      [
        {
          id: "client",
          header: "Client",
          cell: ({
            row: {
              original: { first_name: firstName, last_name: lastName, email },
            },
          }) => (
            <div className="flex flex-1 flex-row items-center gap-2">
              <AimfoxAvatar firstName={firstName} lastName={lastName} />

              <div className="flex flex-col justify-center gap-0.5">
                <span className="line-clamp-1 break-all text-start text-sm-bold">
                  {firstName} {lastName}
                </span>

                <span className="line-clamp-1 break-all text-start text-xs-medium text-black-500">
                  {email}
                </span>
              </div>
            </div>
          ),
        },
        {
          id: "dateAdded",
          header: "Date added",
          cell: ({
            row: {
              original: { created_at: createdAt },
            },
          }) => (
            <span className="text-button-xs text-nowrap">
              {formatBillingDate(createdAt)}
            </span>
          ),
        },
        {
          id: "actions",
          cell: () => (
            <Button intent="labelIcon" size="sm" variant="ghost-black">
              <CaretRight />
            </Button>
          ),
        },
      ] satisfies ColumnDef<AgencyClient>[],
    [],
  );

  return (
    <>
      <MobileListTable
        data={clients}
        isLoading={false}
        columns={columns}
        onRowClick={handleRowClick}
      />
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <OverlayDialogContent>
          <MobileClientDetails client={selectedClient} />
        </OverlayDialogContent>
      </Dialog>
    </>
  );
}
